import React, { useState, useEffect } from "react";

const STATUS_COLOR = {
  Approved: { bg: "#e8faf2", color: "#1a9e5c", dot: "#52c97e" },
  Pending:  { bg: "#fff8e8", color: "#b07d00", dot: "#f5c842" },
  Rejected: { bg: "#fef0ee", color: "#c0392b", dot: "#f97b6b" },
};

const CATEGORY_ICONS = {
  Travel: "✈️", Software: "💻", "Office Supply": "🖊️",
  Marketing: "📣", Meals: "🍽️", Training: "🎓",
};

const CATEGORIES = ["Travel", "Software", "Office Supply", "Marketing", "Meals", "Training", "Other"];
const PAYMENT_METHODS = ["Cash", "Credit Card", "Debit Card", "Bank Transfer", "Cheque"];
const API = process.env.REACT_APP_API_URL || "https://canada-billing.ameontech.com";

const emptyForm = {
  id: null,
  title: "", vendor: "", amount: "", category: "",
  gst: "13", status: "Pending", expense_date: new Date().toISOString().slice(0, 10),
  payment_method: "", notes: "",
};

export default function ExpenseManagement() {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [filter, setFilter]     = useState("All");
  const [search, setSearch]     = useState("");

  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [form, setForm]         = useState(emptyForm);
  const [saving, setSaving]     = useState(false);
  const [formError, setFormError] = useState("");

  const [viewExpense, setViewExpense] = useState(null); // for View modal
  const [deletingId, setDeletingId]   = useState(null); // to show spinner state on delete

  const statuses = ["All", "Approved", "Pending", "Rejected"];

  const fetchExpenses = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/api/expenses`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to fetch expenses");
      const data = await res.json();
      setExpenses(data.expenses || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchExpenses(); }, []);

  const handleFormChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setFormError("");
  };

  // ── OPEN MODAL: ADD ──
  const openAddModal = () => {
    setForm(emptyForm);
    setIsEditMode(false);
    setFormError("");
    setShowModal(true);
  };

  // ── OPEN MODAL: EDIT ──
  const openEditModal = (expense) => {
    setForm({
      id: expense.id,
      title: expense.title || "",
      vendor: expense.vendor || "",
      amount: expense.amount || "",
      category: expense.category || "",
      gst: expense.gst != null ? String(expense.gst) : "13",
      status: expense.status || "Pending",
      expense_date: expense.expense_date ? expense.expense_date.slice(0, 10) : new Date().toISOString().slice(0, 10),
      payment_method: expense.payment_method || "",
      notes: expense.notes || "",
    });
    setIsEditMode(true);
    setFormError("");
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setForm(emptyForm);
    setFormError("");
    setIsEditMode(false);
  };

  // ── SAVE (CREATE or UPDATE) ──
  const handleSaveExpense = async () => {
    if (!form.title || !form.amount || !form.category) {
      setFormError("Title, Amount, and Category are required.");
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const url = isEditMode ? `${API}/api/expenses/${form.id}` : `${API}/api/expenses`;
      const method = isEditMode ? "PUT" : "POST";
      const res = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error(isEditMode ? "Failed to update expense" : "Failed to save expense");
      closeModal();
      await fetchExpenses();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // ── DELETE ──
  const handleDeleteExpense = async (id) => {
    const confirmed = window.confirm("Are you sure you want to delete this expense? This action cannot be undone.");
    if (!confirmed) return;
    setDeletingId(id);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/api/expenses/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to delete expense");
      await fetchExpenses();
    } catch (err) {
      setError(err.message);
    } finally {
      setDeletingId(null);
    }
  };

  const totalExpenses = expenses.reduce((s, e) => s + parseFloat(e.total_amount || 0), 0);
  const approved      = expenses.filter(e => e.status === "Approved").reduce((s, e) => s + parseFloat(e.total_amount || 0), 0);
  const pending       = expenses.filter(e => e.status === "Pending").reduce((s, e) => s + parseFloat(e.total_amount || 0), 0);
  const rejected      = expenses.filter(e => e.status === "Rejected").reduce((s, e) => s + parseFloat(e.total_amount || 0), 0);
  const fmt = (n) => `$${Number(n).toLocaleString("en-CA", { minimumFractionDigits: 2 })}`;

  const filtered = expenses.filter((e) => {
    const matchStatus = filter === "All" || e.status === filter;
    const matchSearch =
      (e.title || "").toLowerCase().includes(search.toLowerCase()) ||
      (e.category || "").toLowerCase().includes(search.toLowerCase()) ||
      (e.vendor || "").toLowerCase().includes(search.toLowerCase());
    return matchStatus && matchSearch;
  });

  const categoryMap = {};
  expenses.forEach((e) => {
    const cat = e.category || "Other";
    if (!categoryMap[cat]) categoryMap[cat] = { total: 0, count: 0 };
    categoryMap[cat].total += parseFloat(e.total_amount || 0);
    categoryMap[cat].count += 1;
  });
  const categoryBreakdown = Object.entries(categoryMap).map(([cat, val]) => ({
    category: cat, total: fmt(val.total), count: val.count,
  }));

  // Preview GST calculation in form
  const previewAmount  = parseFloat(form.amount) || 0;
  const previewGst     = parseFloat(form.gst) || 0;
  const previewTax     = ((previewAmount * previewGst) / 100).toFixed(2);
  const previewTotal   = (previewAmount + parseFloat(previewTax)).toFixed(2);

  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 300, color: "#888", fontSize: 14 }}>
      ⏳ Loading expenses...
    </div>
  );

  if (error) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 300, color: "#f97b6b", fontSize: 14 }}>
      ❌ Error: {error} — Is the server running? (node server.js)
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>

      {/* ── ADD / EDIT MODAL ── */}
      {showModal && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000,
          display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
        }}>
          <div style={{
            background: "#fff", borderRadius: 20, padding: 32, width: "100%", maxWidth: 520,
            boxShadow: "0 20px 60px rgba(0,0,0,0.2)", maxHeight: "90vh", overflowY: "auto",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
              <div style={{ fontFamily: "Playfair Display, serif", fontSize: 18, fontWeight: 800, color: "#1a1a2e" }}>
                {isEditMode ? "Edit Expense" : "Add Expense"}
              </div>
              <button onClick={closeModal}
                style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#888" }}>✕</button>
            </div>

            {formError && (
              <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, padding: "10px 14px", marginBottom: 16, color: "#991b1b", fontSize: 13 }}>
                ❌ {formError}
              </div>
            )}

            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

              {/* Title */}
              <div>
                <label style={labelStyle}>Title *</label>
                <input name="title" value={form.title} onChange={handleFormChange}
                  placeholder="e.g. Flight to Toronto" style={inputStyle} />
              </div>

              {/* Vendor */}
              <div>
                <label style={labelStyle}>Vendor</label>
                <input name="vendor" value={form.vendor} onChange={handleFormChange}
                  placeholder="e.g. Air Canada" style={inputStyle} />
              </div>

              {/* Amount + GST side by side */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <label style={labelStyle}>Amount ($) *</label>
                  <input name="amount" type="number" value={form.amount} onChange={handleFormChange}
                    placeholder="0.00" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>GST/HST (%)</label>
                  <input name="gst" type="number" value={form.gst} onChange={handleFormChange}
                    placeholder="13" style={inputStyle} />
                </div>
              </div>

              {/* GST Preview */}
              {previewAmount > 0 && (
                <div style={{ background: "#f0f7ff", borderRadius: 10, padding: "10px 14px", fontSize: 12, color: "#4f8ef7", display: "flex", gap: 16 }}>
                  <span>Tax: <strong>{fmt(previewTax)}</strong></span>
                  <span>Total: <strong>{fmt(previewTotal)}</strong></span>
                </div>
              )}

              {/* Category */}
              <div>
                <label style={labelStyle}>Category *</label>
                <select name="category" value={form.category} onChange={handleFormChange} style={inputStyle}>
                  <option value="">Select category...</option>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              {/* Date + Payment Method */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <label style={labelStyle}>Date</label>
                  <input name="expense_date" type="date" value={form.expense_date} onChange={handleFormChange} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Payment Method</label>
                  <select name="payment_method" value={form.payment_method} onChange={handleFormChange} style={inputStyle}>
                    <option value="">Select...</option>
                    {PAYMENT_METHODS.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
              </div>

              {/* Status */}
              <div>
                <label style={labelStyle}>Status</label>
                <select name="status" value={form.status} onChange={handleFormChange} style={inputStyle}>
                  <option value="Pending">Pending</option>
                  <option value="Approved">Approved</option>
                  <option value="Rejected">Rejected</option>
                </select>
              </div>

              {/* Notes */}
              <div>
                <label style={labelStyle}>Notes</label>
                <textarea name="notes" value={form.notes} onChange={handleFormChange}
                  placeholder="Optional notes..." rows={3}
                  style={{ ...inputStyle, resize: "vertical" }} />
              </div>

              {/* Buttons */}
              <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
                <button onClick={closeModal}
                  style={{ flex: 1, padding: "11px", borderRadius: 50, border: "1.5px solid #f0f0f0", background: "transparent", color: "#888", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "DM Sans, sans-serif" }}>
                  Cancel
                </button>
                <button onClick={handleSaveExpense} disabled={saving}
                  style={{ flex: 2, padding: "11px", borderRadius: 50, border: "none", background: "linear-gradient(135deg,#4f8ef7,#6366f1)", color: "#fff", fontSize: 13, fontWeight: 600, cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.7 : 1, fontFamily: "DM Sans, sans-serif" }}>
                  {saving ? "Saving..." : isEditMode ? "💾 Update Expense" : "💾 Save Expense"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── VIEW (READ-ONLY) MODAL ── */}
      {viewExpense && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000,
          display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
        }}>
          <div style={{
            background: "#fff", borderRadius: 20, padding: 32, width: "100%", maxWidth: 480,
            boxShadow: "0 20px 60px rgba(0,0,0,0.2)", maxHeight: "90vh", overflowY: "auto",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <div style={{ fontFamily: "Playfair Display, serif", fontSize: 18, fontWeight: 800, color: "#1a1a2e" }}>
                Expense Details
              </div>
              <button onClick={() => setViewExpense(null)}
                style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#888" }}>✕</button>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <ViewRow label="Title" value={viewExpense.title} />
              <ViewRow label="Vendor" value={viewExpense.vendor || "—"} />
              <ViewRow label="Category" value={`${CATEGORY_ICONS[viewExpense.category] || "📋"} ${viewExpense.category || "—"}`} />
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                <ViewRow label="Amount" value={fmt(viewExpense.amount)} />
                <ViewRow label="Tax" value={fmt(viewExpense.tax_amount || 0)} />
                <ViewRow label="Total" value={fmt(viewExpense.total_amount)} bold />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <ViewRow label="Date" value={viewExpense.expense_date?.slice(0, 10) || "—"} />
                <ViewRow label="Payment Method" value={viewExpense.payment_method || "—"} />
              </div>
              <div>
                <label style={labelStyle}>Status</label>
                <span style={{
                  display: "inline-flex", alignItems: "center", gap: 5,
                  padding: "4px 10px", borderRadius: 50, fontSize: 12, fontWeight: 700,
                  background: STATUS_COLOR[viewExpense.status]?.bg || "#f5f5f5",
                  color: STATUS_COLOR[viewExpense.status]?.color || "#888",
                }}>
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: STATUS_COLOR[viewExpense.status]?.dot || "#ccc", display: "inline-block" }} />
                  {viewExpense.status}
                </span>
              </div>
              {viewExpense.notes && <ViewRow label="Notes" value={viewExpense.notes} />}
              {viewExpense.receipt_url && (
                <div>
                  <label style={labelStyle}>Receipt</label>
                  <a href={`${API}${viewExpense.receipt_url}`} target="_blank" rel="noreferrer"
                    style={{ color: "#4f8ef7", fontSize: 13, fontWeight: 600 }}>
                    View Receipt →
                  </a>
                </div>
              )}

              <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
                <button onClick={() => setViewExpense(null)}
                  style={{ flex: 1, padding: "11px", borderRadius: 50, border: "1.5px solid #f0f0f0", background: "transparent", color: "#888", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "DM Sans, sans-serif" }}>
                  Close
                </button>
                <button onClick={() => { const exp = viewExpense; setViewExpense(null); openEditModal(exp); }}
                  style={{ flex: 1, padding: "11px", borderRadius: 50, border: "none", background: "linear-gradient(135deg,#4f8ef7,#6366f1)", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "DM Sans, sans-serif" }}>
                  ✏️ Edit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── SUMMARY CARDS ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
        {[
          { label: "Total Expenses", value: fmt(totalExpenses), color: "#4f8ef7", icon: "💰" },
          { label: "Approved",       value: fmt(approved),      color: "#52c97e", icon: "✅" },
          { label: "Pending",        value: fmt(pending),       color: "#f5c842", icon: "⏳" },
          { label: "Rejected",       value: fmt(rejected),      color: "#f97b6b", icon: "❌" },
        ].map((card) => (
          <div key={card.label} style={{
            background: "#fff", borderRadius: 14, padding: "18px 20px",
            border: "1px solid #f0f0f0", boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
          }}>
            <div style={{ fontSize: 22, marginBottom: 8 }}>{card.icon}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: card.color, fontFamily: "DM Mono, monospace" }}>{card.value}</div>
            <div style={{ fontSize: 12, color: "#888", marginTop: 4, fontWeight: 500 }}>{card.label}</div>
          </div>
        ))}
      </div>

      {/* ── EXPENSES TABLE ── */}
      <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #f0f0f0", overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
        <div style={{ padding: "16px 20px", borderBottom: "1px solid #f5f5f5", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div style={{ fontFamily: "Playfair Display, serif", fontWeight: 700, fontSize: 15, color: "#1a1a2e" }}>
            Expense Records ({filtered.length})
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <input
              placeholder="Search title, category, vendor..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ padding: "7px 14px", borderRadius: 50, border: "1.5px solid #f0f0f0", fontSize: 12, fontFamily: "DM Sans, sans-serif", outline: "none", color: "#1a1a2e", background: "#f8fafc", width: 220 }}
            />
            {statuses.map((s) => (
              <button key={s} onClick={() => setFilter(s)} style={{
                padding: "6px 14px", borderRadius: 50, fontSize: 12, fontWeight: 600,
                border: "1.5px solid", cursor: "pointer", fontFamily: "DM Sans, sans-serif",
                background: filter === s ? "linear-gradient(135deg,#4f8ef7,#6366f1)" : "transparent",
                color: filter === s ? "#fff" : "#888",
                borderColor: filter === s ? "transparent" : "#f0f0f0",
                transition: "all .15s",
              }}>{s}</button>
            ))}
            {/* ADD EXPENSE BUTTON */}
            <button onClick={openAddModal} style={{
              padding: "7px 16px", borderRadius: 50, fontSize: 12, fontWeight: 600,
              border: "none", cursor: "pointer", fontFamily: "DM Sans, sans-serif",
              background: "linear-gradient(135deg,#52c97e,#22c55e)", color: "#fff",
              boxShadow: "0 2px 8px rgba(82,201,126,0.3)",
            }}>
              + Add Expense
            </button>
          </div>
        </div>

        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "#f8fafc" }}>
                {["#", "Title", "Vendor", "Category", "Amount", "GST", "Total", "Date", "Status", "Actions"].map((h) => (
                  <th key={h} style={{ padding: "10px 16px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#aaa", letterSpacing: "0.8px", textTransform: "uppercase", borderBottom: "1px solid #f0f0f0" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={10} style={{ padding: 40, textAlign: "center", color: "#ccc", fontSize: 13 }}>
                    No expenses found. <span style={{ color: "#4f8ef7", cursor: "pointer" }} onClick={openAddModal}>+ Add one</span>
                  </td>
                </tr>
              ) : filtered.map((e, i) => (
                <tr key={e.id} style={{ borderBottom: "1px solid #f5f5f5" }}
                  onMouseEnter={(el) => el.currentTarget.style.background = "#fafafa"}
                  onMouseLeave={(el) => el.currentTarget.style.background = "transparent"}
                >
                  <td style={{ padding: "12px 16px", color: "#bbb", fontWeight: 600 }}>{i + 1}</td>
                  <td style={{ padding: "12px 16px", fontWeight: 600, color: "#1a1a2e" }}>{e.title}</td>
                  <td style={{ padding: "12px 16px", color: "#555" }}>{e.vendor || "—"}</td>
                  <td style={{ padding: "12px 16px", color: "#555" }}>
                    <span style={{ marginRight: 6 }}>{CATEGORY_ICONS[e.category] || "📋"}</span>
                    {e.category || "—"}
                  </td>
                  <td style={{ padding: "12px 16px", color: "#555", fontFamily: "DM Mono, monospace" }}>{fmt(e.amount)}</td>
                  <td style={{ padding: "12px 16px", color: "#888", fontFamily: "DM Mono, monospace" }}>{fmt(e.tax_amount || 0)}</td>
                  <td style={{ padding: "12px 16px", fontWeight: 700, color: "#1a1a2e", fontFamily: "DM Mono, monospace" }}>{fmt(e.total_amount)}</td>
                  <td style={{ padding: "12px 16px", color: "#888" }}>{e.expense_date?.slice(0, 10)}</td>
                  <td style={{ padding: "12px 16px" }}>
                    <span style={{
                      display: "inline-flex", alignItems: "center", gap: 5,
                      padding: "4px 10px", borderRadius: 50, fontSize: 11, fontWeight: 700,
                      background: STATUS_COLOR[e.status]?.bg || "#f5f5f5",
                      color: STATUS_COLOR[e.status]?.color || "#888",
                    }}>
                      <span style={{ width: 6, height: 6, borderRadius: "50%", background: STATUS_COLOR[e.status]?.dot || "#ccc", display: "inline-block" }} />
                      {e.status}
                    </span>
                  </td>
                  {/* ── ACTIONS: VIEW / EDIT / DELETE ── */}
                  <td style={{ padding: "12px 16px" }}>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button
                        title="View"
                        onClick={() => setViewExpense(e)}
                        style={actionBtnStyle("#4f8ef7", "#eaf2ff")}
                      >
                        👁️
                      </button>
                      <button
                        title="Edit"
                        onClick={() => openEditModal(e)}
                        style={actionBtnStyle("#f5c842", "#fff8e8")}
                      >
                        ✏️
                      </button>
                      <button
                        title="Delete"
                        onClick={() => handleDeleteExpense(e.id)}
                        disabled={deletingId === e.id}
                        style={{
                          ...actionBtnStyle("#f97b6b", "#fef0ee"),
                          cursor: deletingId === e.id ? "not-allowed" : "pointer",
                          opacity: deletingId === e.id ? 0.5 : 1,
                        }}
                      >
                        {deletingId === e.id ? "…" : "🗑️"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── CATEGORY BREAKDOWN ── */}
      {categoryBreakdown.length > 0 && (
        <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #f0f0f0", overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
          <div style={{ padding: "16px 20px", borderBottom: "1px solid #f5f5f5" }}>
            <div style={{ fontFamily: "Playfair Display, serif", fontWeight: 700, fontSize: 15, color: "#1a1a2e" }}>Category Breakdown</div>
          </div>
          <div style={{ padding: "16px 20px", display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
            {categoryBreakdown.map((cat) => (
              <div key={cat.category} style={{ background: "#f8fafc", borderRadius: 12, padding: "14px 16px", border: "1px solid #f0f0f0", display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ fontSize: 24 }}>{CATEGORY_ICONS[cat.category] || "📋"}</div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13, color: "#1a1a2e" }}>{cat.category}</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: "#4f8ef7", fontFamily: "DM Mono, monospace" }}>{cat.total}</div>
                  <div style={{ fontSize: 11, color: "#aaa" }}>{cat.count} transactions</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  );
}

// ── Small helper component for read-only fields in View modal ──
function ViewRow({ label, value, bold }) {
  return (
    <div>
      <label style={labelStyle}>{label}</label>
      <div style={{ fontSize: 14, color: "#1a1a2e", fontWeight: bold ? 700 : 500 }}>{value}</div>
    </div>
  );
}

// ── Shared styles ──
const labelStyle = {
  display: "block", fontSize: 12, fontWeight: 600,
  color: "#1a1a2e", marginBottom: 6, fontFamily: "DM Sans, sans-serif",
};
const inputStyle = {
  width: "100%", padding: "10px 14px", borderRadius: 10,
  border: "1.5px solid #e0e0e0", fontSize: 13,
  fontFamily: "DM Sans, sans-serif", outline: "none",
  color: "#1a1a2e", background: "#fff", boxSizing: "border-box",
};
const actionBtnStyle = (color, bg) => ({
  width: 30, height: 30, borderRadius: 8, border: "none",
  background: bg, color, fontSize: 13, cursor: "pointer",
  display: "flex", alignItems: "center", justifyContent: "center",
});